/**
 * ContactDetail.jsx — Full contact detail view (MUI)
 */

import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, ButtonGroup, Chip, Stack, Divider, TextField, MenuItem, MenuList, Select, LinearProgress, Grid, Popper, Grow, ClickAwayListener, IconButton, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { fmtD, truncate, fmtC, STATUS_COLORS, toLocalDateStr } from '../../lib/utils';
import { navigateToJob } from '../../lib/crossNav';
import { useJobs } from '../../hooks/useJobs';
import { useInvoices } from '../../hooks/useInvoices';
import { useDeleteContact, useUpdateContact, useConvertToClient } from '../../hooks/useContacts';
import { useXP } from '../../hooks/useXP';
import { useToast } from '../layout/Toast';
import { useMobile } from '../../hooks/useMobile';
import { useFocus } from '../../hooks/useFocus';
import { useRBAC } from '../../hooks/useRBAC';
import { useSettings } from '../../hooks/useSettings';
import UserAvatar from '../shared/UserAvatar';

const INTERACTION_TYPES = ['Call', 'Email', 'Meeting', 'Text', 'Note'];
const TIER_COLORS = { bronze: '#B06820', silver: '#8B8B8B', gold: '#C9A500', platinum: '#6B7280' };

export default function ContactDetail({ contact, onEdit, onDeleted }) {
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const { data: allJobs = [] } = useJobs();
  const { data: allInvoices = [] } = useInvoices();
  const { mutate: deleteContact, isPending } = useDeleteContact();
  const { mutate: updateContact } = useUpdateContact();
  const convertToClient = useConvertToClient();
  const { getContactXP } = useXP();
  const { toast } = useToast();
  const { isHidden } = useFocus();
  const { hasAccess } = useRBAC();
  const { data: settings } = useSettings();
  // CRM levels below 'full' are the "No $" levels: no revenue or balances here.
  const canSeeFinancials = hasAccess('crm', 'full', settings);

  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [interactionType, setInteractionType] = useState('Call');
  const [interactionNote, setInteractionNote] = useState('');
  const [newJobMenuOpen, setNewJobMenuOpen] = useState(false);
  const newJobAnchorRef = useRef(null);
  const [newJobMenuOpen2, setNewJobMenuOpen2] = useState(false);
  const newJobAnchorRef2 = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload/contact-avatar', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      updateContact({ id: contact.id, data: { ...contact.data, avatar_url: url } },
        { onSuccess: () => toast('Photo updated', 'success'), onError: (err) => toast(`Failed: ${err.message}`, 'error') });
    } catch (err) {
      toast(`Upload failed: ${err.message}`, 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!contact) return <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Select a contact to view details</Typography></Paper>;

  const relatedJobs = allJobs.filter((j) => j.contactId === contact.id);
  const data = contact.data || {};

  const financialSummary = useMemo(() => {
    if (contact.type !== 'client' || relatedJobs.length === 0) return null;
    const totalRevenue = relatedJobs.reduce((sum, j) => sum + (parseFloat(j.revenue) || 0), 0);
    const jobCount = relatedJobs.length;
    const avgJobValue = jobCount > 0 ? totalRevenue / jobCount : 0;
    const jobIds = new Set(relatedJobs.map((j) => j.id));
    // Voided invoices are cancelled paper — they carry a balance but nothing is owed.
    // Bulk invoices carry their jobs in jobIds with a null jobId, so match on both.
    const relatedInvoices = allInvoices.filter((inv) => {
      if (inv.status === 'void') return false;
      if (inv.jobId && jobIds.has(inv.jobId)) return true;
      return (Array.isArray(inv.jobIds) ? inv.jobIds : []).some((id) => jobIds.has(id));
    });
    const totalInvoiced = relatedInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);
    const totalPaid = relatedInvoices.reduce((sum, inv) => sum + (parseFloat(inv.paidAmount) || 0), 0);
    return { totalRevenue, jobCount, avgJobValue, totalInvoiced, totalPaid, outstanding: totalInvoiced - totalPaid };
  }, [contact, relatedJobs, allInvoices]);

  const handleDelete = () => {
    // Invoices can name this contact without any job doing so: bulk invoices,
    // and invoices kept with this client after their job's client changed.
    // Deleting it unlinks them, and they would then follow their job's client.
    const billed = allInvoices.filter((inv) => inv.contactId === contact.id).length;
    const links = [
      relatedJobs.length > 0 && `Linked to ${relatedJobs.length} job(s).`,
      billed > 0 && `Billed on ${billed} invoice(s) — they would lose their client.`,
    ].filter(Boolean);
    const warning = links.length > 0 ? `Delete "${contact.name}"?\n\nWarning: ${links.join(' ')}` : `Delete "${contact.name}"?`;
    if (window.confirm(warning)) {
      deleteContact(contact.id, { onSuccess: () => { toast(`${contact.name} deleted`, 'success'); onDeleted?.(); }, onError: (err) => toast(`Failed: ${err.message}`, 'error') });
    }
  };

  const handleAddInteraction = () => {
    if (!interactionNote.trim()) { toast('Enter a note', 'error'); return; }
    const newInteraction = { type: interactionType, note: interactionNote.trim(), date: toLocalDateStr(new Date()) };
    // Only send data field to avoid overwriting other columns; spread full data to preserve all JSONB fields
    updateContact({ id: contact.id, data: { ...contact.data, interactions: [...(contact.data?.interactions || []), newInteraction] } },
      { onSuccess: () => { toast('Interaction logged', 'success'); setInteractionNote(''); setShowInteractionForm(false); }, onError: (err) => toast(`Failed: ${err.message}`, 'error') });
  };

  const handleNewJob = (mode = 'quick') => navigate(`/jobs?action=newJob&contactId=${contact.id}&jobMode=${mode}`);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflowY: 'auto' }}>
      {/* Header */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, ...(isMobile && { flexWrap: 'wrap' }) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <UserAvatar name={contact.name} size="xl" avatarUrl={contact.data?.avatar_url} contactType={contact.type}
                onClick={() => fileInputRef.current?.click()}
                style={{ cursor: 'pointer' }} />
              <Tooltip title="Change photo">
                <IconButton size="small" onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  sx={{
                    position: 'absolute', bottom: -2, right: -2,
                    bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                    width: 22, height: 22, '&:hover': { bgcolor: 'action.hover' },
                  }}>
                  <CameraAltIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleAvatarUpload} />
            </Box>
            <Box>
            <Typography variant="h6" sx={isMobile ? { fontSize: '1rem' } : undefined}>{contact.name || '(Unnamed)'}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={contact.type} size="small" variant="outlined" />
              {contact.company && <Typography variant="body2" color="text.secondary">{contact.company}</Typography>}
            </Stack>
            </Box>
          </Box>
          <Stack direction="row" spacing={isMobile ? 0.5 : 1} flexWrap={isMobile ? 'wrap' : 'nowrap'} useFlexGap>              {contact.type === 'prospect' && !isHidden('contact.convertToClient') && (
                <Button data-focus="contact.convertToClient" size="small" variant="contained" color="success"
                  onClick={() => {
                    if (!window.confirm(`Convert "${contact.name}" from prospect to client?`)) return;
                    convertToClient.mutate(contact, {
                      onSuccess: () => toast(`${contact.name} is now a client`, 'success'),
                      onError: (err) => toast(`Failed: ${err.message}`, 'error'),
                    });
                  }}
                  disabled={convertToClient.isPending}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Convert to Client
                </Button>
              )}            <ButtonGroup variant="contained" size="small" ref={newJobAnchorRef}>
              <Button onClick={() => handleNewJob('quick')}>+ New Job</Button>
              <Button size="small" onClick={() => setNewJobMenuOpen((p) => !p)} sx={{ px: 0.5, minWidth: 28 }}>
                <ArrowDropDownIcon sx={{ fontSize: 18 }} />
              </Button>
            </ButtonGroup>
            <Popper open={newJobMenuOpen} anchorEl={newJobAnchorRef.current} transition disablePortal sx={{ zIndex: 1300 }}>
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper elevation={4} sx={{ mt: 0.5 }}>
                    <ClickAwayListener onClickAway={() => setNewJobMenuOpen(false)}>
                    <MenuList dense sx={{ py: 0.5 }}>
                      <MenuItem onClick={() => { setNewJobMenuOpen(false); handleNewJob('quick'); }}>Quick Entry</MenuItem>
                      <MenuItem onClick={() => { setNewJobMenuOpen(false); handleNewJob('realestate'); }}>Real Estate</MenuItem>
                      <MenuItem onClick={() => { setNewJobMenuOpen(false); handleNewJob('custom'); }}>Custom</MenuItem>
                    </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
            <Button size="small" variant="outlined" onClick={() => onEdit(contact)}>Edit</Button>
            {!isHidden('contact.delete') && <Button data-focus="contact.delete" size="small" variant="outlined" color="error" onClick={handleDelete} disabled={isPending}>Delete</Button>}
          </Stack>
        </Box>
      </Paper>

      {/* Contact Info */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="overline" sx={{ mb: 1, display: 'block' }}>Contact Info</Typography>
        <Stack spacing={1}>
          {contact.email && <Box sx={{ display: 'flex', gap: 1 }}><Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>Email:</Typography><Typography variant="body2" component="a" href={`mailto:${contact.email}`} color="primary" sx={{ wordBreak: 'break-all', minWidth: 0, '&:hover': { textDecoration: 'underline' } }}>{contact.email}</Typography></Box>}
          {contact.phone && <Box sx={{ display: 'flex', gap: 1 }}><Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>Phone:</Typography><Typography variant="body2" component="a" href={`tel:${contact.phone}`} color="primary">{contact.phone}</Typography></Box>}
          {data.location && <Box sx={{ display: 'flex', gap: 1 }}><Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>Location:</Typography><Typography variant="body2">{data.location}</Typography></Box>}
          {data.web && <Box sx={{ display: 'flex', gap: 1 }}><Typography variant="body2" color="text.secondary" sx={{ width: 80 }}>Website:</Typography><Typography variant="body2" component="a" href={data.web} target="_blank" rel="noopener noreferrer" color="primary" sx={{ wordBreak: 'break-all', minWidth: 0 }}>{truncate(data.web, 40)}</Typography></Box>}
        </Stack>
      </Paper>

      {/* Crew Info */}
      {contact.type === 'crew' && (data.speciality || data.primaryRole || data.inRoster) && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ mb: 1, display: 'block' }}>Crew Info</Typography>
          <Stack spacing={1}>
            {data.speciality && <Box sx={{ display: 'flex', gap: 1 }}><Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>Speciality:</Typography><Typography variant="body2">{data.speciality}</Typography></Box>}
            {data.primaryRole && <Box sx={{ display: 'flex', gap: 1 }}><Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>Primary Role:</Typography><Typography variant="body2">{data.primaryRole}</Typography></Box>}
            {data.inRoster && <Box sx={{ display: 'flex', gap: 1 }}><Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>Roster:</Typography><Chip label="Active" size="small" color="success" sx={{ fontSize: '0.65rem' }} /></Box>}
            {data.payType && <Box sx={{ display: 'flex', gap: 1 }}><Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>Pay Type:</Typography><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{data.payType}</Typography></Box>}
          </Stack>
        </Paper>
      )}

      {/* XP Card */}
      {useMemo(() => {
        const xpData = getContactXP(contact);
        if (xpData.totalXP === 0) return null;
        return (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="overline">XP</Typography>
              <Chip label={xpData.tier.charAt(0).toUpperCase() + xpData.tier.slice(1)} size="small" sx={{ bgcolor: TIER_COLORS[xpData.tier], color: '#fff', fontWeight: 600 }} />
            </Box>
            <LinearProgress variant="determinate" value={100} sx={{ mb: 1, height: 6, borderRadius: 1 }} />
            <Typography variant="body2" color="primary" sx={{ fontWeight: 700, textAlign: 'right', mb: 1 }}>{xpData.totalXP} XP</Typography>
            <Stack spacing={0.5}>
              {xpData.roleXP > 0 && <Stack direction="row" justifyContent="space-between"><Typography variant="caption" color="text.secondary">Role XP:</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{xpData.roleXP}</Typography></Stack>}
              {xpData.engagementXP > 0 && <Stack direction="row" justifyContent="space-between"><Typography variant="caption" color="text.secondary">Engagement XP:</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{xpData.engagementXP}</Typography></Stack>}
              {xpData.revenueXP > 0 && <Stack direction="row" justifyContent="space-between"><Typography variant="caption" color="text.secondary">Revenue XP:</Typography><Typography variant="caption" sx={{ fontWeight: 600 }}>{xpData.revenueXP}</Typography></Stack>}
            </Stack>
          </Paper>
        );
      }, [contact, getContactXP])}

      {/* Financial Summary */}
      {financialSummary && canSeeFinancials && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ mb: 1.5, display: 'block' }}>Financial Summary</Typography>
          <Grid container spacing={2}>
            <Grid size={4} sx={{ textAlign: 'center' }}><Typography variant="h6" color="primary" sx={{ fontSize: { xs: '0.95rem', md: undefined } }}>{fmtC(financialSummary.totalRevenue)}</Typography><Typography variant="caption" color="text.secondary">Total Revenue</Typography></Grid>
            <Grid size={4} sx={{ textAlign: 'center' }}><Typography variant="h6">{financialSummary.jobCount}</Typography><Typography variant="caption" color="text.secondary">Jobs</Typography></Grid>
            <Grid size={4} sx={{ textAlign: 'center' }}><Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', md: undefined } }}>{fmtC(financialSummary.avgJobValue)}</Typography><Typography variant="caption" color="text.secondary">Avg Job Value</Typography></Grid>
          </Grid>
          {financialSummary.outstanding > 0 && (
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">Outstanding Balance</Typography>
              <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>{fmtC(financialSummary.outstanding)}</Typography>
            </Stack>
          )}
        </Paper>
      )}

      {/* Tags */}
      {data.tags?.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ mb: 1, display: 'block' }}>Tags</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {data.tags.map((tag, i) => <Chip key={i} label={tag} size="small" variant="outlined" />)}
          </Stack>
        </Paper>
      )}

      {/* Follow-up */}
      {(data.followUpDate || data.followUpNote) && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ mb: 1, display: 'block' }}>Follow-up</Typography>
          {data.followUpDate && <Typography variant="body2"><strong>Date:</strong> {fmtD(data.followUpDate)}</Typography>}
          {data.followUpNote && <Typography variant="body2">{data.followUpNote}</Typography>}
        </Paper>
      )}

      {/* Notes */}
      {data.notes && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ mb: 1, display: 'block' }}>Notes</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{data.notes}</Typography>
        </Paper>
      )}

      {/* Interactions */}
      {(!isHidden('contact.interactions') || data.interactions?.length > 0) && (
        <Paper data-focus="contact.interactions" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="overline">Interactions ({(data.interactions || []).length})</Typography>
            <Button size="small" onClick={() => setShowInteractionForm(!showInteractionForm)} sx={{ textTransform: 'none' }}>{showInteractionForm ? 'Cancel' : '+ Log Interaction'}</Button>
          </Box>
          {showInteractionForm && (
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Select size="small" value={interactionType} onChange={(e) => setInteractionType(e.target.value)} sx={{ minWidth: 100 }}>
                {INTERACTION_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
              <TextField size="small" fullWidth placeholder="What happened?" value={interactionNote} onChange={(e) => setInteractionNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddInteraction()} />
              <Button variant="contained" size="small" onClick={handleAddInteraction}>Save</Button>
            </Stack>
          )}
          {data.interactions?.length > 0 ? (
            <Stack spacing={1}>
              {data.interactions.slice().reverse().map((inter, i) => (
                <Paper key={i} variant="outlined" sx={{ px: 2, py: 1 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{inter.type || 'Interaction'}</Typography>
                    {inter.date && <Typography variant="caption" color="text.secondary">{fmtD(inter.date)}</Typography>}
                  </Stack>
                  {inter.note && <Typography variant="caption" color="text.secondary">{inter.note}</Typography>}
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.disabled">No interactions recorded yet.</Typography>
          )}
        </Paper>
      )}

      {/* Related Jobs */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="overline">Related Jobs ({relatedJobs.length})</Typography>
          <Box ref={newJobAnchorRef2}
            sx={{ position: 'relative', display: 'inline-flex' }}>
            {isMobile ? (
              <>
                <Button size="small" onClick={() => handleNewJob('quick')} sx={{ textTransform: 'none' }}>+ New Job</Button>
                <IconButton size="small" onClick={() => setNewJobMenuOpen2((p) => !p)} sx={{ width: 40, height: 40 }}><ArrowDropDownIcon /></IconButton>
              </>
            ) : (
              <Button size="small" onClick={() => handleNewJob('quick')} sx={{ textTransform: 'none' }}
                endIcon={<ArrowDropDownIcon sx={{ fontSize: 16, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setNewJobMenuOpen2((p) => !p); }} />}>+ New Job</Button>
            )}
            <Popper open={newJobMenuOpen2} anchorEl={newJobAnchorRef2.current} transition disablePortal sx={{ zIndex: 1300 }}>
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper elevation={4} sx={{ mt: 0.5 }}>
                    <ClickAwayListener onClickAway={() => setNewJobMenuOpen2(false)}>
                    <MenuList dense sx={{ py: 0.5 }}>
                      <MenuItem onClick={() => { setNewJobMenuOpen2(false); handleNewJob('quick'); }}>Quick Entry</MenuItem>
                      <MenuItem onClick={() => { setNewJobMenuOpen2(false); handleNewJob('realestate'); }}>Real Estate</MenuItem>
                      <MenuItem onClick={() => { setNewJobMenuOpen2(false); handleNewJob('custom'); }}>Custom</MenuItem>
                    </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        </Box>
        {relatedJobs.length > 0 ? (
          <Stack spacing={1}>
            {relatedJobs.map((job) => (
              <Paper key={job.id} variant="outlined" onClick={() => navigateToJob(navigate, job.id)} sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.title}</Typography>
                  <Typography variant="caption" color="primary">→</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLORS[job.status] || '#666' }} />
                  <Typography variant="caption" color="text.secondary">{job.status}</Typography>
                  {canSeeFinancials && job.revenue && <Typography variant="caption" color="primary" sx={{ ml: 'auto' }}>{fmtC(job.revenue)}</Typography>}
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography variant="caption" color="text.disabled">No jobs linked to this contact.</Typography>
        )}
      </Paper>
    </Box>
  );
}
